/**
 * Per-ticket-type sale-window status. A ticket type can have its own presale /
 * public-sale / sale-end window, independent of the event's. This is the single
 * source of truth used by BOTH the storefront (to gate the UI) and the server
 * (cart + checkout) so a ticket can't be bought outside its window.
 *
 * Dates are stored as UTC instants; comparisons here are absolute point-in-time
 * comparisons, so timezone is irrelevant to the math.
 */
export interface TicketSaleWindow {
  presaleStartDate?: string | Date | null
  presaleEndDate?: string | Date | null
  publicSaleStartDate?: string | Date | null
  saleEndDate?: string | Date | null
}

export type TicketSaleStatus = 'on_sale' | 'sale_ended' | 'not_yet'

function asDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

export function getTicketSaleStatus(tt: TicketSaleWindow, now: Date = new Date()): TicketSaleStatus {
  const saleEnd = asDate(tt.saleEndDate)
  if (saleEnd && now.getTime() > saleEnd.getTime()) return 'sale_ended'

  const presaleStart = asDate(tt.presaleStartDate)
  const presaleEnd = asDate(tt.presaleEndDate)
  const publicStart = asDate(tt.publicSaleStartDate)

  const inPresale =
    !!presaleStart &&
    now.getTime() >= presaleStart.getTime() &&
    (!presaleEnd || now.getTime() <= presaleEnd.getTime())

  // Not on sale yet: public sale hasn't started and we're not inside a presale.
  if (publicStart && now.getTime() < publicStart.getTime() && !inPresale) return 'not_yet'

  // Presale configured with a future start, no public start -> not yet.
  if (!publicStart && presaleStart && now.getTime() < presaleStart.getTime()) return 'not_yet'

  return 'on_sale'
}

export function isTicketOnSale(tt: TicketSaleWindow, now?: Date): boolean {
  return getTicketSaleStatus(tt, now) === 'on_sale'
}

/** Short human label for an off-sale state (null when on sale). */
export function ticketSaleStatusLabel(status: TicketSaleStatus): string | null {
  if (status === 'sale_ended') return 'Sales ended'
  if (status === 'not_yet') return 'On sale soon'
  return null
}
